<template>
  <div class="snat-unified-tools-panel req-notebook-panel" aria-label="Herramientas SNAT: errores, requerimientos y tickets">
    <button type="button" class="sut-tab" @click="expanded = !expanded">
      <span class="sut-tab-label">SNAT · Ayuda</span>
      <Tag v-if="errorCount" :value="String(errorCount)" severity="danger" class="sut-count" />
      <Tag v-if="reqCount" :value="String(reqCount)" severity="warning" class="sut-count" />
      <Tag v-if="!errorCount && !reqCount" value="·" severity="secondary" class="sut-count sut-count--dot" />
      <i :class="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-up'" aria-hidden="true" />
    </button>

    <Transition name="sut-slide">
      <div v-show="expanded" class="sut-sheet">
        <p class="sut-intro">
          Un solo lugar para el <strong>cuaderno de errores</strong>, el <strong>cuaderno de requerimientos UI</strong> y
          los <strong>tickets SNAT</strong>. En «Requerimientos UI», al crear un ticket puede marcar vaciar el cuaderno.
        </p>
        <TabView v-model:activeIndex="activeTab" class="sut-tabs" :lazy="true">
          <TabPanel header="Errores">
            <ErrorNotebookTrigger mode="embedded" variant="bar" />
          </TabPanel>
          <TabPanel header="Requerimientos UI">
            <RequirementsNotebookPanel embedded />
          </TabPanel>
        </TabView>
      </div>
    </Transition>
  </div>
</template>

<script>
import { watch } from 'vue';
import { mapGetters } from 'vuex';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import Tag from 'primevue/tag';
import ErrorNotebookTrigger from '@/components/ErrorNotebookTrigger.vue';
import RequirementsNotebookPanel from '@/components/RequirementsNotebookPanel.vue';
import { snatUnifiedToolsBus } from '@/utils/snatUnifiedToolsBus';
import { reqNotebookItems } from '@/utils/requirementsNotebook';

export default {
  name: 'SnatUnifiedToolsPanel',
  components: { TabView, TabPanel, Tag, ErrorNotebookTrigger, RequirementsNotebookPanel },
  data() {
    return {
      expanded: false,
      activeTab: 0,
    };
  },
  computed: {
    ...mapGetters('errorNotebook', { errorCount: 'count' }),
    reqCount() {
      return reqNotebookItems.value?.length ?? 0;
    },
  },
  mounted() {
    this._unwatch = watch(
      () => snatUnifiedToolsBus.expandRequestId,
      () => {
        if (!snatUnifiedToolsBus.expandRequestId) return;
        this.expanded = true;
        this.activeTab = snatUnifiedToolsBus.tab === 'requirements' ? 1 : 0;
      }
    );
  },
  beforeUnmount() {
    if (this._unwatch) this._unwatch();
  },
};
</script>

<style scoped>
.snat-unified-tools-panel {
  --sut-ink: #0c2d48;
  --sut-deep: #0a4d7a;
  --sut-mid: #0369a1;
  --sut-soft: #bae6fd;
  --sut-mist: #e0f2fe;

  position: fixed;
  z-index: 10040;
  left: 0;
  bottom: 0;
  max-width: min(560px, 100vw);
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  pointer-events: none;
}

.snat-unified-tools-panel > * {
  pointer-events: auto;
}

.sut-tab {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.85rem;
  margin: 0 0 0.75rem 0.75rem;
  border: 1px solid var(--sut-deep);
  border-radius: 8px 8px 0 0;
  border-bottom: none;
  background: linear-gradient(165deg, var(--sut-mid) 0%, var(--sut-deep) 55%, #082f49 100%);
  box-shadow: 0 -6px 28px rgba(2, 48, 71, 0.35);
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  color: #f0f9ff;
}

.sut-tab:hover {
  background: linear-gradient(165deg, #0284c7 0%, var(--sut-mid) 50%, var(--sut-deep) 100%);
  color: #fff;
}

.sut-tab-label {
  white-space: nowrap;
}

.sut-tab :deep(.p-tag) {
  background: rgba(240, 249, 255, 0.2) !important;
  color: #f0f9ff !important;
  border: 1px solid rgba(186, 230, 253, 0.45);
}

.sut-count {
  font-size: 0.7rem !important;
  padding: 0.15rem 0.45rem !important;
}

.sut-count--dot {
  opacity: 0.65;
  min-width: 1.25rem;
}

.sut-sheet {
  width: 100%;
  max-height: min(78vh, 680px);
  display: flex;
  flex-direction: column;
  padding: 0.65rem 0.75rem 0.85rem;
  margin: 0 0 0 0.75rem;
  background: linear-gradient(180deg, var(--sut-mist) 0%, var(--sut-soft) 120%);
  border: 1px solid var(--sut-mid);
  border-radius: 0 10px 0 0;
  box-shadow: 0 -10px 36px rgba(3, 105, 161, 0.22);
  overflow: hidden;
  color: var(--sut-ink);
}

.sut-intro {
  margin: 0 0 0.5rem;
  font-size: 0.78rem;
  color: #164e63;
  line-height: 1.45;
}

.sut-intro strong {
  color: var(--sut-deep);
}

.sut-tabs {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.sut-tabs :deep(.p-tabview-panels) {
  flex: 1;
  overflow: auto;
  max-height: min(62vh, 560px);
  background: rgba(255, 255, 255, 0.35);
  border-radius: 0 0 8px 8px;
  padding: 0.65rem 0.5rem 0.75rem;
}

.sut-tabs :deep(.p-tabview-nav) {
  border-radius: 8px 8px 0 0;
}

.sut-slide-enter-active,
.sut-slide-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.sut-slide-enter-from,
.sut-slide-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
